<script setup>
import { ref, onMounted } from "vue";
import api from "@/services/api";
import Swal from "sweetalert2";
import MiniStatisticsCard from "@/examples/Cards/MiniStatisticsCard.vue";
import DoughnutChart from "@/examples/Charts/DoughnutChart.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";

// Rol del usuario
const rolUsuario = localStorage.getItem('rol') || 'usuario';

// Suscripción a notificaciones push
const estaSuscrito = ref(false);

// --- ESTADOS REACTIVOS ---
const stats = ref({
  total_activos: 0,
  activos_asignados: 0,
  activos_disponibles: 0,
  activos_mantenimiento: 0,
  total_gerencias: 0
});

const activosPorGerencia = ref([]);
const ultimosActivos = ref([]);
const ultimosUsuarios = ref([]);
const pieEstadoChartData = ref({ labels: [], datasets: [] });
const loading = ref(true);

// Lógica de notificaciones Push
const urlB64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const verificarSuscripcionActual = async () => {
  if ('serviceWorker' in navigator && 'PushManager' in window) {
    try {
      const swRegistration = await navigator.serviceWorker.ready;
      const subscription = await swRegistration.pushManager.getSubscription();
      if (subscription) {
        estaSuscrito.value = true;
      }
    } catch (error) {
      console.error("Error verificando suscripción:", error);
    }
  }
};

const activarNotificacionesPush = async () => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    Swal.fire('No soportado', 'Tu navegador no soporta notificaciones Push.', 'warning');
    return;
  }

  try {
    const swRegistration = await navigator.serviceWorker.register('/custom-sw.js');
    const permission = await Notification.requestPermission();

    if (permission !== 'granted') {
      throw new Error('El usuario denegó los permisos.');
    }

    const PUBLIC_VAPID_KEY = 'BCuFk6djlf11O_4LN6zgGh6JzJovo3TjwovqdF73APTXO87kUSz3vDYg4_N0_PPfVZgJ5HB63rnN6Q3NRZZrUlw';
    const applicationServerKey = urlB64ToUint8Array(PUBLIC_VAPID_KEY);

    const subscription = await swRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey
    });

    const subJson = subscription.toJSON();

    const payloadDjango = {
      status_type: 'subscribe',
      subscription: {
        endpoint: subJson.endpoint,
        keys: {
          auth: subJson.keys.auth,
          p256dh: subJson.keys.p256dh
        }
      },
      browser: navigator.userAgent
    };

    await api.post('notificaciones/suscribir/', payloadDjango);
    estaSuscrito.value = true;

    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: 'Alertas de TI activadas',
      showConfirmButton: false,
      timer: 3000
    });

  } catch (error) {
    console.error('Error al suscribir notificaciones:', error);
    Swal.fire('Atención', 'Debes permitir las notificaciones en tu navegador para recibir alertas.', 'info');
  }
};

const getDashboardData = async () => {
  try {
    loading.value = true;
    const response = await api.get("dashboard/stats/");
    const res = response.data;

    stats.value = res.cards || stats.value;
    activosPorGerencia.value = res.activos_por_gerencia || [];
    ultimosActivos.value = res.ultimos_activos || [];
    ultimosUsuarios.value = res.ultimos_usuarios || [];

    if (res.charts && res.charts.estado_fisico) {
      pieEstadoChartData.value = {
        labels: res.charts.estado_fisico.labels,
        datasets: res.charts.estado_fisico.datasets
      };
    }

  } catch (error) {
    console.error("Error cargando Dashboard:", error.response?.data || error.message);
  } finally {
    loading.value = false;
  }
};

const getBadgeSituacionClass = (situacion) => {
  switch (situacion) {
    case 'Asignado':
      return 'bg-gradient-success';
    case 'Disponible':
      return 'bg-gradient-info';
    case 'Mantenimiento':
      return 'bg-gradient-warning';
    case 'Baja':
      return 'bg-gradient-danger';
    default:
      return 'bg-gradient-secondary';
  }
};

const getBadgeEstadoClass = (estado) => {
  switch (estado) {
    case 'Bueno':
      return 'text-success';
    case 'Regular':
      return 'text-warning';
    case 'Malo':
      return 'text-danger';
    default:
      return 'text-secondary';
  }
};

onMounted(() => {
  getDashboardData();
  verificarSuscripcionActual();
});
</script>

<template>
  <div class="py-4 container-fluid">
    <div v-if="loading" class="py-4">
      <div class="row">
        <div v-for="i in 4" :key="'kpi-dash-'+i" class="col-xl-3 col-sm-6 mb-xl-0 mb-4">
          <SkeletonLoader type="card" :count="1" />
        </div>
      </div>
      <div class="row mt-4">
        <div class="col-lg-7 mb-4">
          <SkeletonLoader type="card" :count="1" />
        </div>
        <div class="col-lg-5 mb-4">
          <SkeletonLoader type="card" :count="1" />
        </div>
      </div>
    </div>

    <div v-else>
      <div class="row mb-3" v-if="(rolUsuario === 'admin_informatico' || rolUsuario === 'admin_soporte') && !estaSuscrito">
        <div class="col-12 d-flex justify-content-end">
          <button @click="activarNotificacionesPush" class="btn btn-danger shadow-sm mb-0">
            <i class="fas fa-bell me-2"></i> Activar Alertas de Tickets
          </button>
        </div>
      </div>

      <!-- CARDS KPI PRINCIPALES -->
      <div class="row">
        <div class="col-xl-3 col-sm-6 mb-xl-0 mb-4">
          <mini-statistics-card
            title="Total Activos"
            :value="stats.total_activos.toString()"
            description="<span class='text-sm font-weight-bolder text-primary'>Registrados</span> en sistema"
            :icon="{ component: 'ni ni-box-2', background: 'bg-gradient-primary' }"
          />
        </div>

        <div class="col-xl-3 col-sm-6 mb-xl-0 mb-4">
          <mini-statistics-card
            title="Activos Asignados"
            :value="stats.activos_asignados.toString()"
            description="<span class='text-sm font-weight-bolder text-success'>En uso</span> por funcionarios"
            :icon="{ component: 'ni ni-single-02', background: 'bg-gradient-success' }"
          />
        </div>

        <div class="col-xl-3 col-sm-6 mb-xl-0 mb-4">
          <mini-statistics-card
            title="Activos Disponibles"
            :value="stats.activos_disponibles.toString()"
            description="<span class='text-sm font-weight-bolder text-info'>Listos</span> para asignación"
            :icon="{ component: 'ni ni-check-bold', background: 'bg-gradient-info' }"
          />
        </div>

        <div class="col-xl-3 col-sm-6">
          <mini-statistics-card
            title="En Mantenimiento"
            :value="stats.activos_mantenimiento.toString()"
            description="<span class='text-sm font-weight-bolder text-warning'>En revisión / reparación</span>"
            :icon="{ component: 'ni ni-settings', background: 'bg-gradient-warning' }"
          />
        </div>
      </div>

      <!-- FILA 1: ACTIVOS POR GERENCIA + ESTADO FÍSICO -->
      <div class="row mt-4">
        <!-- COLUMNA IZQUIERDA: ACTIVOS POR GERENCIA -->
        <div class="col-lg-7 mb-4">
          <div class="card h-100 shadow-sm">
            <div class="p-3 pb-0 card-header d-flex justify-content-between align-items-center">
              <div>
                <h6 class="mb-0">Distribución de Activos por Gerencia</h6>
                <p class="text-xs text-secondary mb-0">Total acumulado y porcentaje sobre el parque total</p>
              </div>
              <span class="badge bg-gradient-primary">{{ stats.total_gerencias }} Gerencias</span>
            </div>
            <div class="card-body p-3">
              <div class="table-responsive">
                <table class="table align-items-center mb-0">
                  <thead>
                    <tr>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-3">Gerencia</th>
                      <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Unidades</th>
                      <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Porcentaje</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in activosPorGerencia" :key="item.nombre">
                      <td class="ps-3">
                        <div class="d-flex align-items-center">
                          <div class="icon icon-shape icon-xs me-2 bg-gradient-dark text-center border-radius-sm">
                            <i class="ni ni-building text-white opacity-10" style="font-size: 10px;"></i>
                          </div>
                          <span class="text-sm font-weight-bold text-dark">{{ item.nombre }}</span>
                        </div>
                      </td>
                      <td class="text-center text-sm">
                        <span class="badge bg-light text-dark font-weight-bold">{{ item.cantidad }}</span>
                      </td>
                      <td class="align-middle text-center">
                        <div class="d-flex align-items-center justify-content-center px-3">
                          <span class="me-2 text-xs font-weight-bold">{{ item.porcentaje }}%</span>
                          <div class="progress w-100" style="height: 8px;">
                            <div
                              class="progress-bar bg-gradient-primary"
                              role="progressbar"
                              :style="{ width: item.porcentaje + '%' }"
                            ></div>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="activosPorGerencia.length === 0">
                      <td colspan="3" class="text-center text-secondary py-3 text-sm">
                        No hay activos registrados por gerencia
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- COLUMNA DERECHA: DISTRIBUCIÓN POR ESTADO FÍSICO -->
        <div class="col-lg-5 mb-4">
          <div class="card h-100 shadow-sm">
            <div class="p-3 pb-0 card-header">
              <h6 class="mb-0">Estado Físico de Activos</h6>
              <p class="text-xs text-secondary mb-0">Condición general del equipamiento</p>
            </div>
            <div class="card-body p-3 d-flex justify-content-center align-items-center">
              <doughnut-chart
                v-if="pieEstadoChartData.labels && pieEstadoChartData.labels.length > 0"
                :chart="pieEstadoChartData"
                :height="240"
                centerText="Estado"
              />
              <div v-else class="text-center text-secondary py-5">
                Cargando estado físico...
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- FILA 2: ÚLTIMOS ACTIVOS REGISTRADOS + ÚLTIMOS USUARIOS CONECTADOS -->
      <div class="row mt-2">
        <!-- ÚLTIMOS ACTIVOS REGISTRADOS -->
        <div class="col-lg-7 mb-4">
          <div class="card h-100 shadow-sm">
            <div class="p-3 pb-0 card-header d-flex justify-content-between align-items-center">
              <div>
                <h6 class="mb-0">Últimos Activos Registrados</h6>
                <p class="text-xs text-secondary mb-0">Equipos recientemente incorporados al sistema</p>
              </div>
              <router-link to="/gestion-activos" class="btn btn-xs btn-outline-primary mb-0">
                Ver Todos <i class="fas fa-arrow-right ms-1"></i>
              </router-link>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table align-items-center mb-0">
                  <thead>
                    <tr>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-4">Código / Nombre</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Gerencia</th>
                      <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Situación</th>
                      <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Estado</th>
                      <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Fecha Reg.</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="activo in ultimosActivos" :key="activo.id">
                      <td class="ps-4">
                        <div class="d-flex flex-column">
                          <h6 class="mb-0 text-sm font-weight-bold">{{ activo.nombre }}</h6>
                          <span class="text-xs text-secondary">{{ activo.codigo }}</span>
                        </div>
                      </td>
                      <td>
                        <div class="d-flex flex-column">
                          <span class="text-xs font-weight-bold text-dark">{{ activo.gerencia }}</span>
                          <span class="text-xxs text-secondary">{{ activo.seccion }}</span>
                        </div>
                      </td>
                      <td class="text-center text-sm">
                        <span class="badge badge-sm" :class="getBadgeSituacionClass(activo.situacion)">
                          {{ activo.situacion }}
                        </span>
                      </td>
                      <td class="text-center text-sm">
                        <span class="text-xs font-weight-bold" :class="getBadgeEstadoClass(activo.estado_fisico)">
                          {{ activo.estado_fisico }}
                        </span>
                      </td>
                      <td class="text-center text-sm">
                        <span class="text-xs text-secondary font-weight-bold">{{ activo.fecha_registro }}</span>
                      </td>
                    </tr>
                    <tr v-if="ultimosActivos.length === 0">
                      <td colspan="5" class="text-center text-secondary py-3 text-sm">
                        No hay activos recientes registrados
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- ÚLTIMOS USUARIOS CONECTADOS -->
        <div class="col-lg-5 mb-4">
          <div class="card h-100 shadow-sm">
            <div class="p-3 pb-0 card-header d-flex justify-content-between align-items-center">
              <div>
                <h6 class="mb-0">Últimos Usuarios Conectados</h6>
                <p class="text-xs text-secondary mb-0">Inicios de sesión e historial de acceso reciente</p>
              </div>
              <span class="badge bg-gradient-success">Acceso Reciente</span>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table align-items-center mb-0">
                  <thead>
                    <tr>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-3">Usuario</th>
                      <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Último Acceso</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="user in ultimosUsuarios" :key="user.id">
                      <td class="ps-3 py-2">
                        <div class="d-flex align-items-center">
                          <div class="avatar avatar-sm me-2 bg-gradient-primary rounded-circle text-white d-flex align-items-center justify-content-center" style="width: 30px; height: 30px; font-size: 11px;">
                            <i class="ni ni-single-02 text-white"></i>
                          </div>
                          <div class="d-flex flex-column">
                            <h6 class="mb-0 text-xs font-weight-bold">{{ user.nombre_completo }}</h6>
                            <span class="text-xxs text-secondary">@{{ user.username }}</span>
                          </div>
                        </div>
                      </td>
                      <td class="text-center text-sm py-2">
                        <span class="text-xs text-secondary font-weight-bold">{{ user.last_login }}</span>
                      </td>
                    </tr>
                    <tr v-if="ultimosUsuarios.length === 0">
                      <td colspan="2" class="text-center text-secondary py-3 text-sm">
                        No hay registros de accesos recientes
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>